import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { useCreateZoneCategoryMutation, useUpdateZoneCategoryMutation, useZoneCategories } from 'store/apis';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices';

interface UseZoneCategoryFormManagerProps {
  categoryId?: number;
  next?: (data?: any) => void;
}

export const useZoneCategoryFormManager = ({ categoryId: id, next = () => {} }: UseZoneCategoryFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const { data: zoneCategories, isLoading: loading_zoneCategories } = useZoneCategories();
  const [createZoneCategory, { isLoading: loading_createZoneCategory }] = useCreateZoneCategoryMutation();
  const [updateZoneCategory, { isLoading: loading_updateZoneCategory }] = useUpdateZoneCategoryMutation();

  const zoneCategory = id ? zoneCategories.find((item) => item.id === id) || ({} as any) : ({} as any);
  const mutate = id ? updateZoneCategory : createZoneCategory;

  const initialValues = {
    category: zoneCategory.category || '',
    target_interval: zoneCategory.target_interval || 'daily',
    target_frequency: zoneCategory.target_frequency || 1,
  };

  const validationSchema = Yup.object({
    category: Yup.string().nullable().required('Zone category name is required.'),
    target_interval: Yup.string().nullable().required('Target interval must be selected.'),
    target_frequency: Yup.number()
      .nullable()
      .required('Input target frequency value.')
      .min(1, 'The value must be greater than 0'),
  });

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (value) => {
      const mutateProps = id ? { id, orgSlug, ...value } : { orgSlug, ...value };

      mutate(mutateProps as any)
        .then(() => {
          next();
        })
        .catch((err) => {
          console.error('error = ', err);
        })
        .finally();
    },
  });

  const loading_form = id ? loading_zoneCategories : false;
  const loading_submit = id ? loading_updateZoneCategory : loading_createZoneCategory;

  return [
    {
      formManager,
      loading_form,
      loading_submit,
    },
  ] as [
    {
      formManager: FormikProps<typeof initialValues>;
      loading_form: boolean;
      loading_submit: boolean;
    },
  ];
};
